import { useEffect, useMemo, useRef } from "react";

import { grey } from "@mui/material/colors";
import ReactFlow, { Background, Controls, useEdgesState, useNodesState } from "react-flow-renderer";

import FlowNode from "@plannel/pages/factory-plan/common/component/FlowNode";

/**
 * FP 플로우차트(다이어그램) 공용 래퍼 — old `wingui-core/component/workflow/component/FLODiagram.jsx`
 * 중 **읽기 전용 표시에 필요한 부분만** 옮긴 것.
 *
 * old FLODiagram은 그룹 만들기/복사·붙여넣기/엣지 타입 변경/PNG 저장까지 갖춘 범용 편집기(879줄)지만,
 * FLO 화면은 그걸 `ActionBarHidden` + `viewMode`로 감싸 쓰기 때문에 편집 기능이 애초에 노출되지
 * 않는다(reports/flo-diagram-investigation.md §0). 그래서 편집기를 통째로 포팅하지 않고,
 * new 기존 flow 화면(`components/flow/*`)과 같은 패턴으로 `ReactFlow`를 직접 쓴다.
 *
 * 노드 좌표는 호출부가 `common/flowchart.js`의 `layoutFlow`로 미리 계산해서 넘긴다.
 * 사용자가 노드를 드래그해 옮길 수 있고(old 동일), 그 위치는 **저장되지 않는다** — 재조회하면
 * `layoutFlow`가 계산한 자동 배치로 돌아간다(old도 저장 경로가 없었다).
 *
 * props:
 *   - nodes/edges : reactflow 노드·엣지(좌표 포함)
 *   - onNodeDoubleClick(event, node) : 노드 더블클릭(딥링크 이동 등). 없으면 비활성
 */
const FlowChart = ({ nodes, edges, onNodeDoubleClick }) => {
  const nodeTypes = useMemo(() => ({ floNode: FlowNode }), []);
  const instanceRef = useRef(null);

  // 드래그로 바뀐 위치는 react-flow가 onNodesChange로 통지하므로 내부 상태로 들고 있어야 한다
  // (props를 그대로 <ReactFlow nodes>에 넘기면 드래그해도 화면이 안 움직인다).
  // 대신 조회 결과(props)가 바뀔 때마다 내부 상태를 통째로 갈아끼워 자동 배치로 되돌린다.
  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState([]);
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => { setFlowNodes(nodes); }, [nodes, setFlowNodes]);
  useEffect(() => { setFlowEdges(edges); }, [edges, setFlowEdges]);

  // old Flo.jsx:60-65 — 노드가 새로 그려질 때마다 전체가 보이도록 맞춘다.
  // 의존성은 내부 상태가 아니라 **props**여야 한다 — 내부 상태로 걸면 드래그할 때마다
  // fitView가 다시 돌아 사용자가 옮긴 화면이 튄다.
  useEffect(() => {
    if (!instanceRef.current || nodes.length === 0) return;
    // 노드 DOM이 실제로 붙은 뒤에 계산되도록 한 프레임 미룬다(old도 setTimeout으로 지연시켰다).
    const timer = setTimeout(() => instanceRef.current?.fitView({ padding: 0.2 }), 100);
    return () => clearTimeout(timer);
  }, [nodes]);

  return (
    <ReactFlow
      nodes={flowNodes}
      edges={flowEdges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      onInit={(instance) => { instanceRef.current = instance; }}
      onNodeDoubleClick={onNodeDoubleClick}
      // old FLODiagram(editMode=true)과 동일하게 드래그로 노드를 옮길 수 있다(저장은 없음).
      nodesDraggable
      nodesConnectable={false}
      // ⚠️ elementsSelectable을 false로 두지 마라. react-flow-renderer v10은 노드 래퍼의
      // pointer-events를 `isSelectable || isDraggable || onClick || onMouseEnter ||
      // onMouseMove || onMouseLeave`로만 판단한다(dist/esm/index.js). onNodeDoubleClick은
      // 이 목록에 없어서, 셋 다 false면 pointer-events:none이 걸려 **툴팁도 더블클릭 딥링크도
      // 통째로 죽는다.** old도 editMode=true라 selectable이 켜져 있었다.
      elementsSelectable
      zoomOnScroll
      // TODO(MIG-T2): [flo/bom/ordertracking] 다이어그램 영역 선택의 **조작법**이 old와 다르다.
      //   old(FLODiagram.jsx:723-726, reactflow v11): panOnDrag:[2](우클릭 드래그로만 화면 이동),
      //     selectionOnDrag:true(좌드래그로 영역 선택), selectionMode:Partial(걸치면 선택),
      //     multiSelectionKeyCode:'Shift'
      //   new(react-flow-renderer v10.3.17): **네 옵션 모두 없다.** panOnDrag는 boolean 전용이고
      //     d3-zoom 필터가 `... && !event.button`이라 **좌클릭만 팬**으로 하드코딩돼 있다.
      //     선택 박스는 `selectionKeyPressed`(selectionKeyCode 기본 'Shift')일 때만 뜨고,
      //     포함 판정도 `getNodesInside(..., false, true)`로 "완전 포함"이 박혀 있다.
      //   사유: 맞추려면 reactflow v11 교체가 필요한데, FP 밖 공통 컴포넌트
      //     `src/components/flow/{AccessControlFlow,BfSettingFlow,DistributionNetworkFlow}.js`가
      //     같은 패키지를 써서 그쪽까지 회귀 검증 대상이 된다(2026-08-14 사용자 결정: v10 유지).
      // 기능 자체는 v10 기본값으로 동작한다 — **Shift + 좌드래그**로 영역 선택,
      // 선택 묶음을 드래그하면 함께 이동한다(NodesSelection).
      panOnDrag
      fitView
    >
      <Background gap={16} color={grey[300]} />
      {/* old `FLODiagram.jsx:814`와 동일한 구성 — 확대/축소/화면맞춤 + **상호작용 잠금 토글**
          (`showInteractive` 기본값 true). old는 미니맵을 기본 비노출(`showMinimap ?? false`)로 뒀고
          FLO 계열 화면은 켜지 않았으므로 미니맵 버튼은 두지 않는다(2026-08-12 사용자 지시).
          ⚠️ 잠금 토글은 nodesDraggable/elementsSelectable/nodesConnectable을 한꺼번에 끈다 —
          잠근 동안에는 §14-5대로 노드 툴팁·더블클릭 딥링크도 같이 멈춘다(old도 동일). */}
      <Controls />
    </ReactFlow>
  );
};

export default FlowChart;
